import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { taskNavRef } from '../utils/taskNav';

/**
 * Рабочее место преподавателя:
 * - вкладка «Ученики» со списком связей;
 * - вкладка «Списки задач» (TaskList преподавателя);
 * - вкладка «Уроки» (LessonSession преподавателя).
 */
export default function TeacherDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('students');

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState('');

  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentOverview, setStudentOverview] = useState(null);
  const [studentOverviewLoading, setStudentOverviewLoading] = useState(false);
  const [studentOverviewError, setStudentOverviewError] = useState('');

  const [taskLists, setTaskLists] = useState([]);
  const [taskListsLoading, setTaskListsLoading] = useState(true);
  const [taskListsError, setTaskListsError] = useState('');

  const [editingList, setEditingList] = useState(null); // null — ничего, {} — новый, объект — существующий
  const [listForm, setListForm] = useState({
    title: '',
    studentId: '',
    deadline: '',
    items: [], // { taskId, slug, title, teacherNote }
  });
  const [listSaving, setListSaving] = useState(false);
  const [listFormError, setListFormError] = useState('');

  const [taskSearch, setTaskSearch] = useState('');
  const [taskSearchResults, setTaskSearchResults] = useState([]);
  const [taskSearchLoading, setTaskSearchLoading] = useState(false);
  const [taskSearchError, setTaskSearchError] = useState('');

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [lessonsError, setLessonsError] = useState('');
  const [lessonReportSessionId, setLessonReportSessionId] = useState(null);
  const [lessonReport, setLessonReport] = useState(null);
  const [lessonReportLoading, setLessonReportLoading] = useState(false);
  const [endingLessonId, setEndingLessonId] = useState(null);

  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteNote, setInviteNote] = useState('');

  const fetchStudents = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setStudentsLoading(true);
      setStudentsError('');
    }
    try {
      const res = await api.get('/teacher/students/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setStudents(data);
      if (!silent) setStudentsError('');
    } catch {
      if (!silent) setStudentsError('Не удалось загрузить список учеников.');
    } finally {
      if (!silent) setStudentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchTaskLists();
    fetchLessons();
  }, [fetchStudents]);

  async function fetchInvites() {
    setInvitesLoading(true);
    setInvitesError('');
    try {
      const res = await api.get('/auth/teacher-invitations/');
      setInvites(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setInvitesError('Не удалось загрузить приглашения.');
    } finally {
      setInvitesLoading(false);
    }
  }

  async function fetchTaskLists() {
    setTaskListsLoading(true);
    setTaskListsError('');
    try {
      const res = await api.get('/teacher/task-lists/');
      setTaskLists(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setTaskListsError('Не удалось загрузить списки задач преподавателя.');
    } finally {
      setTaskListsLoading(false);
    }
  }

  async function fetchLessons() {
    setLessonsLoading(true);
    setLessonsError('');
    try {
      const res = await api.get('/teacher/lesson-sessions/');
      setLessons(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setLessonsError('Не удалось загрузить уроки.');
    } finally {
      setLessonsLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'students') {
      fetchInvites();
    }
  }, [activeTab]);

  /** Обновляем статус «К работе» без полноэкранной загрузки, пока открыта вкладка учеников. */
  useEffect(() => {
    if (activeTab !== 'students') return;
    const id = setInterval(() => fetchStudents({ silent: true }), 15000);
    return () => clearInterval(id);
  }, [activeTab, fetchStudents]);

  async function loadStudentOverview(studentId) {
    setSelectedStudentId(studentId);
    setStudentOverview(null);
    setStudentOverviewError('');
    setStudentOverviewLoading(true);
    try {
      const res = await api.get(`/teacher/students/${studentId}/overview/`);
      setStudentOverview(res.data);
    } catch {
      setStudentOverviewError('Не удалось загрузить сводку по ученику.');
    } finally {
      setStudentOverviewLoading(false);
    }
  }

  async function handleStartLesson(studentId) {
    try {
      const res = await api.post('/teacher/lesson-sessions/', { student_id: studentId });
      // Обновляем список уроков и сводку по ученику
      await fetchLessons();
      if (selectedStudentId === studentId) {
        await loadStudentOverview(studentId);
      }
      return res.data;
    } catch (e) {
      // Ошибку показываем только кратко
      alert(e.response?.data?.detail || 'Не удалось запустить урок.');
      return null;
    }
  }

  function getInviteUrl(token) {
    if (!token) return '';
    if (typeof window === 'undefined') return `/invite/${token}`;
    return `${window.location.origin}/invite/${token}`;
  }

  function startEditList(lst) {
    setEditingList(lst);
    setListForm({
      title: lst.title || '',
      studentId: '', // менять ученика для существующего списка пока не даём
      deadline: lst.deadline_at ? lst.deadline_at.slice(0, 10) : '',
      items: Array.isArray(lst.items)
        ? lst.items.map((it) => ({
            taskId: it.task?.id,
            slug: it.task ? taskNavRef(it.task) : '',
            title: it.task?.title,
            teacherNote: it.teacher_note || '',
          }))
        : [],
    });
    setListFormError('');
    setTaskSearch('');
    setTaskSearchResults([]);
    setTaskSearchError('');
  }

  function addTaskToList(task) {
    if (!task?.id) return;
    setListForm((prev) => {
      if (prev.items.some((it) => it.taskId === task.id)) return prev;
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            taskId: task.id,
            slug: taskNavRef(task),
            title: task.title,
            teacherNote: '',
          },
        ],
      };
    });
  }

  function removeTaskFromList(taskId) {
    setListForm((prev) => ({
      ...prev,
      items: prev.items.filter((it) => it.taskId !== taskId),
    }));
  }

  function updateItemNote(taskId, note) {
    setListForm((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.taskId === taskId ? { ...it, teacherNote: note } : it
      ),
    }));
  }

  async function searchTasks() {
    if (!taskSearch.trim()) {
      setTaskSearchResults([]);
      setTaskSearchError('');
      return;
    }
    setTaskSearchLoading(true);
    setTaskSearchError('');
    try {
      const params = new URLSearchParams();
      params.set('search', taskSearch.trim());
      const res = await api.get(`/tasks/editor/?${params.toString()}`);
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setTaskSearchResults(data);
    } catch {
      setTaskSearchError('Не удалось выполнить поиск задач.');
      setTaskSearchResults([]);
    } finally {
      setTaskSearchLoading(false);
    }
  }

  async function handleSaveList() {
    if (!editingList) return;
    if (!listForm.title.trim()) {
      setListFormError('Введите название списка.');
      return;
    }
    if (!editingList.id && !listForm.studentId) {
      setListFormError('Выберите ученика.');
      return;
    }

    setListSaving(true);
    setListFormError('');
    try {
      const payload = {
        title: listForm.title.trim(),
        deadline_at: listForm.deadline || null,
        items: listForm.items.map((it, index) => ({
          task_id: it.taskId,
          position: index,
          teacher_note: it.teacherNote || '',
        })),
      };
      if (!editingList.id) {
        payload.student = Number(listForm.studentId);
        await api.post('/teacher/task-lists/', payload);
      } else {
        await api.patch(`/teacher/task-lists/${editingList.id}/`, payload);
      }
      await fetchTaskLists();
      setEditingList(null);
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setListFormError(
          first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка сохранения списка.'
        );
      } else {
        setListFormError('Ошибка сохранения списка.');
      }
    } finally {
      setListSaving(false);
    }
  }

  function renderStudentsTab() {
    return (
      <div className="teacher-tab teacher-students">
        <div className="teacher-tab-main">
          <h2>Ученики</h2>
          <p className="text-secondary teacher-students-hint" style={{ maxWidth: '44rem', marginBottom: '1rem' }}>
            Чтобы появилась кнопка «К работе», ученик должен открыть задачу{' '}
            <strong>из вашего списка ДЗ</strong> (страница <code>/work/…</code> с параметром{' '}
            <code>list=…</code> в адресе) и дождаться загрузки редактора. Если ученик зашёл с «Мои задачи»
            без списка или только что открыл вкладку — кнопки не будет; список обновляется автоматически
            каждые 15 с. Та же ссылка есть в карточке ученика (кнопка «Подключиться к работе»).
          </p>
          {studentsError && <div className="alert alert-error">{studentsError}</div>}
          {studentsLoading ? (
            <p className="text-secondary">Загрузка списка учеников...</p>
          ) : students.length === 0 ? (
            <p className="text-secondary">Пока нет учеников в связке с вами.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Ученик</th>
                  <th>Статус связи</th>
                  <th>Списки задач</th>
                  <th>Активный урок</th>
                  <th>Онлайн</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr
                    key={s.id}
                    className={selectedStudentId === s.id ? 'selected' : ''}
                    onClick={() => navigate(`/teacher/students/${s.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{s.first_name || s.last_name ? `${s.last_name} ${s.first_name}`.trim() : s.username}</td>
                    <td>{s.relation_status}</td>
                    <td>{s.task_lists_count}</td>
                    <td>{s.has_active_lesson_session ? 'Да' : 'Нет'}</td>
                    <td>{s.is_online ? 'Да' : 'Нет'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/teacher/students/${s.id}`);
                        }}
                        style={{ marginRight: '0.5rem' }}
                      >
                        Журнал
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/teacher/billing/${s.id}`);
                        }}
                        style={{ marginRight: '0.5rem' }}
                      >
                        Оплата
                      </button>
                      {s.work_join_url ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginRight: '0.5rem' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(s.work_join_url);
                          }}
                          title="Режим наблюдения: та же задача, список и совместная сессия редактора, что у ученика"
                        >
                          К работе
                        </button>
                      ) : (
                        <span
                          className="text-secondary"
                          style={{ fontSize: '0.8rem', marginRight: '0.5rem', display: 'inline-block', maxWidth: '7rem', verticalAlign: 'middle' }}
                          title="Ученик не на странице работы из списка ДЗ или редактор ещё не подключился к совместной сессии"
                        >
                          нет связи
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartLesson(s.id);
                        }}
                      >
                        Начать урок
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="teacher-tab-sidebar">
          <div className="student-overview-block">
            <h3>Приглашения</h3>
            {invitesError && <div className="alert alert-error">{invitesError}</div>}
            <div className="form-group">
              <label>Создать ссылку-приглашение</label>
              <div className="invite-create-row">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Комментарий (необязательно)"
                  value={inviteNote}
                  onChange={(e) => setInviteNote(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={creatingInvite}
                  onClick={async () => {
                    setCreatingInvite(true);
                    setInvitesError('');
                    try {
                      const res = await api.post('/auth/teacher-invitations/', {
                        note: inviteNote || undefined,
                      });
                      const created = res.data;
                      const url = getInviteUrl(created.token);
                      try {
                        await navigator.clipboard.writeText(url);
                        // eslint-disable-next-line no-alert
                        alert('Ссылка скопирована в буфер обмена.');
                      } catch {
                        // eslint-disable-next-line no-alert
                        alert(url);
                      }
                      setInviteNote('');
                      await fetchInvites();
                    } catch (e) {
                      const detail = e.response?.data?.detail || 'Не удалось создать приглашение.';
                      setInvitesError(detail);
                    } finally {
                      setCreatingInvite(false);
                    }
                  }}
                >
                  {creatingInvite ? 'Создание…' : 'Создать ссылку'}
                </button>
              </div>
            </div>
            {invitesLoading ? (
              <p className="text-secondary">Загрузка приглашений...</p>
            ) : invites.length === 0 ? (
              <p className="text-secondary">Пока нет активных приглашений.</p>
            ) : (
              <ul className="list-simple">
                {invites.map((inv) => (
                  <li key={inv.id}>
                    <div className="invite-item">
                      <div>
                        <span className="text-secondary">
                          Создано:{' '}
                          {inv.created_at
                            ? new Date(inv.created_at).toLocaleString('ru-RU')
                            : ''}
                        </span>
                        {inv.expires_at && (
                          <span className="text-secondary">
                            , действует до{' '}
                            {new Date(inv.expires_at).toLocaleString('ru-RU')}
                          </span>
                        )}
                      </div>
                      <div className="invite-link-row">
                        <code className="invite-url">{getInviteUrl(inv.token)}</code>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline"
                          onClick={async () => {
                            const url = getInviteUrl(inv.token);
                            try {
                              await navigator.clipboard.writeText(url);
                              // eslint-disable-next-line no-alert
                              alert('Ссылка скопирована в буфер обмена.');
                            } catch {
                              // eslint-disable-next-line no-alert
                              alert(url);
                            }
                          }}
                        >
                          Копировать
                        </button>
                      </div>
                      {inv.note && (
                        <div className="text-secondary">Комментарий: {inv.note}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <h3>Сводка по ученику</h3>
          {studentOverviewLoading && <p className="text-secondary">Загрузка...</p>}
          {studentOverviewError && <div className="alert alert-error">{studentOverviewError}</div>}
          {!studentOverviewLoading && !studentOverview && !studentOverviewError && (
            <p className="text-secondary">Выберите ученика слева, чтобы посмотреть детали.</p>
          )}
          {studentOverview && (
            <div className="student-overview">
              <div className="student-overview-header">
                <strong>
                  {studentOverview.student.last_name} {studentOverview.student.first_name}
                </strong>
                <div className="student-overview-sub">
                  @{studentOverview.student.username}
                </div>
              </div>

              <div className="student-overview-block">
                <h4>Активный урок</h4>
                {studentOverview.active_lesson_session ? (
                  <div className="badge">
                    Урок #{studentOverview.active_lesson_session.id} с{' '}
                    {new Date(studentOverview.active_lesson_session.started_at).toLocaleString('ru-RU')}
                  </div>
                ) : (
                  <p className="text-secondary">Сейчас урок не идёт.</p>
                )}
              </div>

              <div className="student-overview-block">
                <h4>Списки задач</h4>
                {studentOverview.task_lists.length === 0 ? (
                  <p className="text-secondary">Для этого ученика ещё нет списков задач.</p>
                ) : (
                  <ul className="list-simple">
                    {studentOverview.task_lists.map((lst) => {
                      const firstItem = Array.isArray(lst.items) && lst.items.length > 0 ? lst.items[0] : null;
                      const firstTaskSlug = firstItem?.task ? taskNavRef(firstItem.task) : null;
                      const hasActiveLesson = Boolean(studentOverview.active_lesson_session);
                      const workUrl = firstTaskSlug
                        ? hasActiveLesson
                          ? `/sessions/${studentOverview.active_lesson_session.id}/tasks/${firstTaskSlug}?list=${lst.id}`
                          : `/work/${firstTaskSlug}?list=${lst.id}`
                        : null;
                      return (
                        <li key={lst.id}>
                          <span>{lst.title}</span>{' '}
                          <span className="badge">
                            {lst.student_status}
                            {lst.student_finished_at ? ' • Сдано' : ''}
                          </span>{' '}
                          {workUrl && (
                            <Link to={workUrl} className="btn btn-xs btn-outline">
                              Открыть в работе
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="student-overview-block">
                <h4>Последние попытки</h4>
                {studentOverview.recent_attempts.length === 0 ? (
                  <p className="text-secondary">Пока нет зафиксированных попыток.</p>
                ) : (
                  <ul className="list-simple">
                    {studentOverview.recent_attempts.map((a) => (
                      <li key={a.id}>
                        <span>{a.task_title}</span>{' '}
                        <span className="badge">{a.self_report_status || '—'}</span>{' '}
                        <span className="text-secondary">
                          {a.created_at ? new Date(a.created_at).toLocaleString('ru-RU') : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderTaskListsTab() {
    return (
      <div className="teacher-tab teacher-tasklists">
        <div className="teacher-tasklists-header">
          <div>
            <h2>Списки задач</h2>
            <p className="text-secondary">
              Списки задач, которые вы выдавали ученикам, и заготовки. Создайте новый список или заготовку на отдельной странице.
            </p>
          </div>
          <div className="teacher-tasklists-header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/teacher/lists/create')}
              disabled={students.length === 0}
            >
              Создать список
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/teacher/lists/create?mode=template')}
            >
              Создать заготовку
            </button>
          </div>
        </div>
        {taskListsError && <div className="alert alert-error">{taskListsError}</div>}
        <div className="teacher-tasklists-content">
          <div className="teacher-tasklists-list">
            {taskListsLoading ? (
              <p className="text-secondary">Загрузка списков задач...</p>
            ) : taskLists.length === 0 ? (
              <p className="text-secondary">Списков задач пока нет.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Ученик</th>
                    <th>Выдано</th>
                    <th>Дедлайн</th>
                    <th>Статус ученика</th>
                    <th>Статус преподавателя</th>
                    <th>Задачи</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {taskLists.map((lst) => (
                    <tr key={lst.id}>
                      <td>{lst.title}</td>
                      <td>{lst.is_template ? 'Заготовка' : (lst.student ?? '—')}</td>
                      <td>{lst.issued_at ? new Date(lst.issued_at).toLocaleDateString('ru-RU') : '—'}</td>
                      <td>{lst.deadline_at ? new Date(lst.deadline_at).toLocaleDateString('ru-RU') : '—'}</td>
                      <td>
                        {lst.student_status}
                        {lst.student_finished_at ? (
                          <div className="text-secondary" style={{ fontSize: '0.85rem' }}>
                            Сдано: {new Date(lst.student_finished_at).toLocaleString('ru-RU')}
                          </div>
                        ) : null}
                      </td>
                      <td>{lst.teacher_status}</td>
                      <td>{Array.isArray(lst.items) ? lst.items.length : 0}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline"
                          onClick={() => startEditList(lst)}
                        >
                          Редактировать
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {editingList && (
            <div className="teacher-tasklists-form">
              <h3>{editingList.id ? 'Редактирование списка задач' : 'Новый список задач'}</h3>
              {listFormError && <div className="alert alert-error">{listFormError}</div>}

              {!editingList.id && (
                <div className="form-group">
                  <label>Ученик</label>
                  <select
                    className="form-select"
                    value={listForm.studentId}
                    onChange={(e) =>
                      setListForm((prev) => ({ ...prev, studentId: e.target.value }))
                    }
                  >
                    <option value="">Выберите ученика</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.first_name || s.last_name
                          ? `${s.last_name} ${s.first_name}`.trim()
                          : s.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Название списка</label>
                <input
                  className="form-input"
                  type="text"
                  value={listForm.title}
                  onChange={(e) =>
                    setListForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Например, «ДЗ 12/03»"
                />
              </div>

              <div className="form-group">
                <label>Дедлайн</label>
                <input
                  className="form-input"
                  type="date"
                  value={listForm.deadline}
                  onChange={(e) =>
                    setListForm((prev) => ({ ...prev, deadline: e.target.value }))
                  }
                />
              </div>

              <div className="form-group">
                <label>Задачи в списке</label>
                {listForm.items.length === 0 ? (
                  <p className="text-secondary">Пока нет задач. Добавьте задачи через поиск ниже.</p>
                ) : (
                  <ul className="list-simple">
                    {listForm.items.map((it) => (
                      <li key={it.taskId}>
                        <div className="tasklist-item-row">
                          <span>
                            {it.title || it.slug || (it.taskId != null ? `ID ${it.taskId}` : '')}
                          </span>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Комментарий преподавателя (необязательно)"
                            value={it.teacherNote}
                            onChange={(e) => updateItemNote(it.taskId, e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            onClick={() => removeTaskFromList(it.taskId)}
                          >
                            Удалить
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group">
                <label>Поиск задач для добавления</label>
                <div className="task-search-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Поиск по названию/условию"
                    value={taskSearch}
                    onChange={(e) => setTaskSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchTasks();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={searchTasks}
                    disabled={taskSearchLoading}
                  >
                    {taskSearchLoading ? 'Поиск…' : 'Найти'}
                  </button>
                </div>
                {taskSearchError && (
                  <div className="alert alert-error">{taskSearchError}</div>
                )}
                {taskSearchResults.length > 0 && (
                  <div className="task-search-results">
                    <ul className="list-simple">
                      {taskSearchResults.slice(0, 20).map((t) => (
                        <li key={t.id}>
                          <div className="task-search-item">
                            <span>
                              {t.title || taskNavRef(t)}{' '}
                              <span className="text-secondary">({taskNavRef(t)})</span>
                            </span>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={() => addTaskToList(t)}
                            >
                              Добавить
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveList}
                  disabled={listSaving}
                >
                  {listSaving ? 'Сохранение…' : 'Сохранить список'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setEditingList(null)}
                  disabled={listSaving}
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const REPORT_LABELS = { EASY: '++', OK_NO_PEEK: '+', OK_WITH_PEEK: '+-', OK_WITH_QUESTION: '+?', FAILED: '-', NO_IDEAS: '--', NOT_TRIED: '!' };

  async function loadLessonReport(sessionId) {
    if (lessonReportSessionId === sessionId && lessonReport) {
      setLessonReportSessionId(null);
      setLessonReport(null);
      return;
    }
    setLessonReportSessionId(sessionId);
    setLessonReport(null);
    setLessonReportLoading(true);
    try {
      const res = await api.get(`/teacher/lesson-sessions/${sessionId}/report/`);
      setLessonReport(res.data);
    } catch {
      setLessonReport(null);
    } finally {
      setLessonReportLoading(false);
    }
  }

  function renderLessonsTab() {
    return (
      <div className="teacher-tab teacher-lessons">
        <h2>Уроки</h2>
        <p className="text-secondary">
          Активные и недавние уроки (LessonSession), которые вы проводили.
        </p>
        {lessonsError && <div className="alert alert-error">{lessonsError}</div>}
        {lessonsLoading ? (
          <p className="text-secondary">Загрузка уроков...</p>
        ) : lessons.length === 0 ? (
          <p className="text-secondary">Уроков пока нет.</p>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Ученик</th>
                  <th>Начало</th>
                  <th>Окончание</th>
                  <th>Статус</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((s) => (
                  <tr key={s.id}>
                    <td>{s.id}</td>
                    <td>{s.student_username}</td>
                    <td>{s.started_at ? new Date(s.started_at).toLocaleString('ru-RU') : '—'}</td>
                    <td>{s.ended_at ? new Date(s.ended_at).toLocaleString('ru-RU') : '—'}</td>
                    <td>{s.ended_at ? 'Завершён' : 'Активен'}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                        {!s.ended_at && (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            disabled={endingLessonId === s.id || lessonReportLoading}
                            onClick={async () => {
                              if (!window.confirm('Завершить урок?')) return;
                              setEndingLessonId(s.id);
                              try {
                                await api.post(`/tasks/lesson-sessions/${s.id}/end/`);
                                await fetchLessons();
                                if (lessonReportSessionId === s.id) {
                                  try {
                                    const res = await api.get(`/teacher/lesson-sessions/${s.id}/report/`);
                                    setLessonReport(res.data);
                                  } catch {
                                    /* ignore */
                                  }
                                }
                              } catch {
                                alert('Не удалось завершить урок.');
                              } finally {
                                setEndingLessonId(null);
                              }
                            }}
                          >
                            {endingLessonId === s.id ? '…' : 'Завершить'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-xs btn-outline"
                          onClick={() => loadLessonReport(s.id)}
                          disabled={lessonReportLoading}
                        >
                          {lessonReportSessionId === s.id ? 'Скрыть отчёт' : 'Отчёт'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lessonReportSessionId && (
              <div className="lesson-report-block">
                {lessonReportLoading ? (
                  <p className="text-secondary">Загрузка отчёта...</p>
                ) : lessonReport ? (
                  <div>
                    <h4>Отчёт по уроку #{lessonReport.session_id} ({lessonReport.student_username})</h4>
                    <p className="text-secondary">
                      {lessonReport.started_at ? new Date(lessonReport.started_at).toLocaleString('ru-RU') : ''}
                      {lessonReport.ended_at ? ` — ${new Date(lessonReport.ended_at).toLocaleString('ru-RU')}` : ''}
                    </p>
                    {lessonReport.teacher_notes?.trim() ? (
                      <div
                        className="lesson-teacher-notes"
                        style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}
                      >
                        <strong>Заметки по уроку</strong>
                        <p style={{ margin: '0.35rem 0 0' }}>{lessonReport.teacher_notes}</p>
                      </div>
                    ) : null}
                    {lessonReport.items?.length === 0 ? (
                      <p className="text-secondary">В этом уроке пока нет зафиксированных попыток по задачам.</p>
                    ) : (
                      <ul className="lesson-report-items">
                        {lessonReport.items.map((item) => (
                          <li key={item.attempt_id}>
                            <strong>{item.task_title || item.task_slug}</strong>
                            {' — '}
                            <span className="report-badge">{REPORT_LABELS[item.self_report_status] || item.self_report_status || '—'}</span>
                            {item.duration_minutes != null && ` (${item.duration_minutes} мин)`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <p className="text-secondary">Не удалось загрузить отчёт.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="teacher-dashboard">
      <div className="page-header">
        <h1>Кабинет преподавателя</h1>
        <p className="page-subtitle">
          Управляйте учениками, списками задач и уроками.
        </p>
        <div className="teacher-dashboard-header-tools">
          <Link to="/teacher/tags" className="btn btn-outline btn-sm">
            Справочник тегов
          </Link>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          Ученики
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'tasklists' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasklists')}
        >
          Списки задач
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'lessons' ? 'active' : ''}`}
          onClick={() => setActiveTab('lessons')}
        >
          Уроки
        </button>
      </div>

      {activeTab === 'students' && renderStudentsTab()}
      {activeTab === 'tasklists' && renderTaskListsTab()}
      {activeTab === 'lessons' && renderLessonsTab()}
    </div>
  );
}

